import type { AppliedSchemaMapping } from "../../hooks/useAppliedFieldMapping";

export interface HeaderCoverage {
  /** Detected headers that successfully mapped to a schema field. */
  mapped: string[];
  /** Detected headers that the parser saw but no mapping consumed. */
  unmapped: string[];
}

/**
 * Compute mapped / unmapped split of detected source headers.
 *
 * Used by `TransformationHeader` to surface a coverage summary like
 * `17 columns detected · 12 mapped · 5 unmapped`, with the unmapped list
 * expandable so reviewers can see exactly which source columns the parser
 * found but didn't route. Today this only surfaces in raw transformation
 * data (`processing_metadata.detected_headers`) — reviewers wondering "did
 * the parser see my column?" have to open the source file to check.
 *
 * Comparison is case-insensitive and trim-tolerant. Header parsers may
 * normalize casing or whitespace, and a "Check Date" header should match a
 * mapping whose `detectedHeader` is `"check date"`. Original casing from
 * `detectedHeaders` is preserved in the returned arrays.
 *
 * @param detectedHeaders Raw headers from `processing_metadata.detected_headers`.
 * @param schemaMappings  All schema mappings from `useAppliedFieldMapping`.
 *                        Mapped headers are collected across every schema.
 */
export const buildHeaderCoverage = (
  detectedHeaders: string[] | undefined,
  schemaMappings: AppliedSchemaMapping[]
): HeaderCoverage => {
  if (!detectedHeaders || detectedHeaders.length === 0) {
    return { mapped: [], unmapped: [] };
  }

  const mappedKeys = new Set<string>();
  for (const schemaMapping of schemaMappings) {
    for (const mapping of schemaMapping.mappings) {
      mappedKeys.add(normalize(mapping.detectedHeader));
    }
  }

  const mapped: string[] = [];
  const unmapped: string[] = [];
  for (const header of detectedHeaders) {
    if (mappedKeys.has(normalize(header))) {
      mapped.push(header);
    } else {
      unmapped.push(header);
    }
  }

  return { mapped, unmapped };
};

/** Trim + lowercase. Centralized so the comparison rule is testable. */
const normalize = (s: string): string => s.trim().toLowerCase();
